import { Slider, Typography, Input, InputLabel, FormControl, TextField } from '@material-ui/core';
import { isArray } from '@material-ui/data-grid';
import { ChangeEvent, ReactElement } from 'react';

interface ChangeAllPlacementSliderProps {
	label: string;
	fromValue: number;
	toValue: number;
	min: number;
	max: number;
	step?: number;
	marks?: Array<{ value: number; label: string }>;
	valueLabelFormat?: (value: number) => string;
	onValuesChanged: (from: number, to: number) => void;
	type?: 'number' | 'time';
}

const ChangeAllPlacementSlider = (props: ChangeAllPlacementSliderProps): ReactElement => {
	const {
		label,
		fromValue,
		toValue,
		min,
		max,
		step = 1,
		marks,
		valueLabelFormat,
		onValuesChanged,
		type = 'number',
	} = props;

	const handleSliderChanged = (_: ChangeEvent<{}>, newValue: number | number[]): void => {
		if (isArray(newValue)) {
			onValuesChanged(newValue[0], newValue[1]);
		}
	};

	const getTimestamp = (value: string): number => {
		const splittedValue = value.split(':');
		const hours = Number(splittedValue[0]);
		const minutes = Number(splittedValue[1]);
		const seconds = Number(splittedValue[2]);
		return (hours * 60 + minutes) * 60 + seconds;
	};

	const handleFromChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const value = type === 'time' ? getTimestamp(target.value) : Number(target.value);
		if (!isNaN(value)) {
			const clampedValue = Math.max(min, Math.min(max, value));
			if (clampedValue > toValue) {
				onValuesChanged(clampedValue, clampedValue);
			} else {
				onValuesChanged(clampedValue, toValue);
			}
		}
	};

	const handleToChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const value = type === 'time' ? getTimestamp(target.value) : Number(target.value);
		if (!isNaN(value)) {
			const clampedValue = Math.max(min, Math.min(max, value));
			if (clampedValue < fromValue) {
				onValuesChanged(clampedValue, clampedValue);
			} else {
				onValuesChanged(fromValue, clampedValue);
			}
		}
	};

	return (
		<>
			<FormControl fullWidth={true} margin="normal">
				<Typography id="range-slider" gutterBottom>
					{label}
				</Typography>
				<Slider
					value={[fromValue, toValue]}
					onChange={handleSliderChanged}
					marks={marks}
					valueLabelDisplay="auto"
					valueLabelFormat={valueLabelFormat}
					min={min}
					max={max}
					step={step}
				/>
			</FormControl>
			{type === 'number' ? (
				<>
					<FormControl fullWidth={true} margin="normal">
						<InputLabel required={true}>From</InputLabel>
						<Input
							onChange={handleFromChanged}
							value={fromValue}
							required={true}
							inputProps={{
								step: step,
								min: min,
								max: max,
								type: 'number',
							}}
						/>
					</FormControl>
					<FormControl fullWidth={true} margin="normal">
						<InputLabel required={true}>To</InputLabel>
						<Input
							onChange={handleToChanged}
							value={toValue}
							required={true}
							inputProps={{
								step: step,
								min: min,
								max: max,
								type: 'number',
							}}
						/>
					</FormControl>
				</>
			) : (
				<>
					<FormControl fullWidth={true} margin="normal">
						<TextField
							label="From"
							type="time"
							inputProps={{
								step: step,
								max: max,
								min: min,
							}}
							value={valueLabelFormat ? valueLabelFormat(fromValue) : fromValue}
							onChange={handleFromChanged}
						/>
					</FormControl>
					<FormControl fullWidth={true} margin="normal">
						<TextField
							label="To"
							type="time"
							inputProps={{
								step: step,
								max: max,
								min: min,
							}}
							value={valueLabelFormat ? valueLabelFormat(toValue) : toValue}
							onChange={handleToChanged}
						/>
					</FormControl>
				</>
			)}
		</>
	);
};

export default ChangeAllPlacementSlider;
